import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import SearchBar from "../components/SearchBar";
import ImageCard from "../components/cards/ImageCard";
import { GetPosts } from "../api";
import { CircularProgress } from "@mui/material";
import { 
  AutoAwesome, 
  Brush, 
  Speed, 
  Security,
  Email,
  Psychology,
  ArrowForward,
  SmartToy,
  AutoFixHigh,
  Twitter,
  LinkedIn,
  GitHub,
  Instagram,
  Facebook,
  Phone,
  LocationOn,
  Send,
  BrowseGallery,
  BrowserUpdated,
  BroadcastOnHome,
  AdbOutlined,
  Web
} from "@mui/icons-material";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const gradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(151, 71, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(151, 71, 255, 0.6);
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  background: ${({ theme }) => theme.bg};
  scroll-behavior: smooth;
`;

// Hero Section
const HeroSection = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 30px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
    min-height: 90vh;
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 0;
  object-fit: cover;
  opacity: 0.15;
  filter: blur(2px);
  
  @media (max-width: 768px) {
    opacity: 0.1;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.bg} 0%,
    transparent 20%,
    transparent 80%,
    ${({ theme }) => theme.bg} 100%
  );
  z-index: 1;
`;

const GradientBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 30%, rgba(151, 71, 255, 0.2) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(0, 122, 255, 0.2) 0%, transparent 50%);
  z-index: 2;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  text-align: center;
  z-index: 3;
  animation: ${fadeInUp} 1s ease-out;
  position: relative;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => `${theme.secondary}20`};
  border: 1px solid ${({ theme }) => `${theme.secondary}40`};
  padding: 8px 20px;
  border-radius: 50px;
  margin-bottom: 30px;
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  backdrop-filter: blur(10px);
  
  svg {
    color: ${({ theme }) => theme.secondary};
    font-size: 18px;
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 20px 0;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.text_primary} 0%, 
    ${({ theme }) => theme.secondary} 50%,
    ${({ theme }) => theme.primary} 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradient} 8s ease infinite;
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
  
  @media (max-width: 480px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 24px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};
  margin: 0 0 40px 0;
  max-width: 800px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin: 0 0 30px 0;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.secondary}, 
    ${({ theme }) => theme.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 60px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PrimaryButton = styled.button`
  padding: 16px 40px;
  font-size: 18px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, ${({ theme }) => theme.secondary}, ${({ theme }) => theme.primary});
  color: ${({ theme }) => theme.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  animation: ${glow} 2s ease-in-out infinite;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 40px rgba(151, 71, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 14px 32px;
    font-size: 16px;
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
`;

const SecondaryButton = styled.button`
  padding: 16px 40px;
  font-size: 18px;
  font-weight: 600;
  border: 2px solid ${({ theme }) => theme.secondary};
  border-radius: 12px;
  background: transparent;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: ${({ theme }) => `${theme.secondary}20`};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 14px 32px;
    font-size: 16px;
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
`;

const TechStack = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const TechLabel = styled.span`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  font-weight: 500;
`;

const TechIcon = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.card};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => `${theme.secondary}20`};
  transition: all 0.3s ease;
  
  svg {
    font-size: 28px;
    color: ${({ theme }) => theme.secondary};
  }
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.secondary};
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    
    svg {
      font-size: 24px;
    }
  }
`;

// Features Section
const FeaturesSection = styled.div`
  padding: 100px 30px;
  background: ${({ theme }) => theme.bgLight};
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.text_primary};
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.text_secondary};
  text-align: center;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FeatureCard = styled.div`
  padding: 40px;
  background: ${({ theme }) => theme.card};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => `${theme.secondary}10`};
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: ${({ delay }) => delay || '0s'};
  opacity: 0;
  animation-fill-mode: forwards;
  
  &:hover {
    transform: translateY(-10px);
    border-color: ${({ theme }) => theme.secondary};
    box-shadow: 0 20px 60px rgba(151, 71, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    ${({ theme }) => `${theme.secondary}20`}, 
    ${({ theme }) => `${theme.primary}20`});
  border-radius: 16px;
  margin-bottom: 20px;
  
  svg {
    font-size: 32px;
    color: ${({ theme }) => theme.secondary};
  }
`;

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: ${({ theme }) => theme.text_primary};
`;

const FeatureDescription = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  margin: 0;
`;

// Gallery Section
const GallerySection = styled.div`
  padding: 100px 30px;
  background: ${({ theme }) => theme.bg};
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SearchWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto 60px auto;
  
  @media (max-width: 768px) {
    margin: 0 auto 40px auto;
  }
`;

const CardWrapper = styled.div`
  display: grid;
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: 640px) and (max-width: 1199px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 639px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.red};
  text-align: center;
  padding: 40px;
  font-size: 18px;
`;

const NoPostsMessage = styled.div`
  text-align: center;
  padding: 60px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 18px;
`;

// Footer Section
const FooterSection = styled.footer`
  background: ${({ theme }) => theme.bgLight};
  border-top: 1px solid ${({ theme }) => `${theme.secondary}20`};
  padding: 80px 30px 30px 30px;
  
  @media (max-width: 768px) {
    padding: 60px 20px 20px 20px;
  }
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 60px;
  margin-bottom: 60px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, ${({ theme }) => theme.secondary}, ${({ theme }) => theme.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 10px;
`;

const FooterDescription = styled.p`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const FooterTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 10px 0;
`;

const FooterLink = styled.a`
  color: ${({ theme }) => theme.text_secondary};
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    color: ${({ theme }) => theme.secondary};
    transform: translateX(5px);
  }
  
  svg {
    font-size: 18px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const SocialIcon = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => `${theme.secondary}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text_secondary};
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.secondary}, ${({ theme }) => theme.primary});
    border-color: transparent;
    color: ${({ theme }) => theme.white};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(151, 71, 255, 0.3);
  }
  
  svg {
    font-size: 20px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  line-height: 1.6;
  
  svg {
    font-size: 20px;
    margin-top: 2px;
    color: ${({ theme }) => theme.secondary};
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${({ theme }) => `${theme.secondary}10`};
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  margin: 0;
  
  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.secondary}, ${({ theme }) => theme.primary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 600;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 30px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
  }
`;

const FooterBottomLink = styled.a`
  color: ${({ theme }) => theme.text_secondary};
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.secondary};
  }
`;

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filteredPost, setFilteredPost] = useState([]);
  const navigate = useNavigate();

  const getPosts = async () => {
    setLoading(true);
    await GetPosts()
      .then((res) => {
        setPosts(res?.data?.data);
        setFilteredPost(res?.data?.data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error?.response?.data?.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    getPosts();
  }, []);

  useEffect(() => {
    if (!search) {
      setFilteredPost(posts);
    }
    const filteredPosts = posts.filter((post) => {
      const promptMatch = post?.prompt?.toLowerCase().includes(search);
      const authorMatch = post?.name?.toLowerCase().includes(search);

      return promptMatch || authorMatch;
    });

    if (search) {
      setFilteredPost(filteredPosts);
    }
  }, [posts, search]);

  const features = [
    {
      icon: <Psychology />,
      title: "AI-Powered Generation",
      description: "Advanced AI models create stunning, unique images from your text prompts in seconds.",
      delay: "0.1s"
    },
    {
      icon: <Speed />,
      title: "Lightning Fast",
      description: "Generate high-quality images in seconds with our optimized AI pipeline.",
      delay: "0.2s"
    },
    {
      icon: <SmartToy />,
      title: "AI Automation",
      description: "Automated workflows with n8n integration for seamless image generation and processing.",
      delay: "0.3s"
    },
    {
      icon: <Email />,
      title: "Email Delivery",
      description: "Generated images are automatically sent to your email - instant access anywhere, anytime.",
      delay: "0.4s"
    },
    {
      icon: <Security />,
      title: "Secure & Private",
      description: "Your data and creations are protected with enterprise-grade security.",
      delay: "0.5s"
    },
    {
      icon: <AutoAwesome />,
      title: "Community Gallery",
      description: "Share your creations and get inspired by the community's artwork.",
      delay: "0.6s"
    }
  ];

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <VideoBackground
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="aivideo.mp4" type="video/mp4" />
          <source src="https://assets.mixkit.co/videos/preview/mixkit-abstract-digital-particles-4240-large.mp4" type="video/mp4" />
        </VideoBackground>
        <VideoOverlay />
        <GradientBackground />
        <HeroContent>
          <Badge>
            <AutoAwesome />
            Introducing AI Image Generator
          </Badge>
          
          <HeroTitle>
            Create Stunning Images
            <br />
            with AI Magic
          </HeroTitle>
          
          <HeroSubtitle>
            Transform your ideas into <GradientText>breathtaking visuals</GradientText> with the power of artificial intelligence. 
            Just describe what you want, and watch the magic happen.
          </HeroSubtitle>
          
          <ButtonGroup>
            <PrimaryButton onClick={() => navigate("/post")}>
              Start Creating
              <ArrowForward />
            </PrimaryButton>
            <SecondaryButton onClick={() => document.getElementById('gallery').scrollIntoView({ behavior: 'smooth' })}>
              View Gallery
            </SecondaryButton>
          </ButtonGroup>
          
          <TechStack>
            <TechLabel>Powered by</TechLabel>
            <TechIcon><Psychology /></TechIcon>
            <TechIcon><SmartToy /></TechIcon>
            <TechIcon><Email /></TechIcon>
            <TechIcon><AutoFixHigh /></TechIcon>
          </TechStack>
        </HeroContent>
      </HeroSection>

      {/* Features Section */}
      <FeaturesSection>
        <SectionTitle>Powerful Features</SectionTitle>
        <SectionSubtitle>
          Everything you need to create amazing AI-generated artwork
        </SectionSubtitle>
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index} delay={feature.delay}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      {/* Gallery Section */}
      <GallerySection id="gallery">
        <SectionTitle>Community Gallery</SectionTitle>
        <SectionSubtitle>
          Explore amazing creations from our community
        </SectionSubtitle>
        
        <SearchWrapper>
          <SearchBar
            search={search}
            handleChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrapper>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {loading ? (
          <LoadingWrapper>
            <CircularProgress style={{ color: '#9747FF' }} />
          </LoadingWrapper>
        ) : (
          <CardWrapper>
            {filteredPost.length > 0 ? (
              <>
                {filteredPost.map((item, index) => (
                  <ImageCard 
                    key={item.id || index} 
                    item={item}
                  />
                ))}
              </>
            ) : (
              <NoPostsMessage>No posts found. Be the first to create!</NoPostsMessage>
            )}
          </CardWrapper>
        )}
      </GallerySection>

      {/* Footer Section */}
      <FooterSection>
        <FooterContent>
          {/* Company Info */}
          <FooterColumn>
            <FooterLogo>
              <AutoAwesome />
              ImagineAI
            </FooterLogo>
            <FooterDescription>
              Transform your imagination into stunning visuals with the power of AI. 
              Create, share, and explore unlimited possibilities.
            </FooterDescription>
            <SocialLinks>
            <SocialIcon href="https://www.linkedin.com/in/mohammad-oves/" target="_blank" rel="noopener noreferrer">
                <LinkedIn />
              </SocialIcon>
              <SocialIcon href="https://github.com/MohdOves" target="_blank" rel="noopener noreferrer">
                <GitHub />
              </SocialIcon>
              <SocialIcon href="https://x.com/MohdOves07" target="_blank" rel="noopener noreferrer">
                <Twitter />
              </SocialIcon>
              <SocialIcon href="https://mdoves.me" target="_blank" rel="noopener noreferrer">
                <Web/>
              </SocialIcon>
              
              
            </SocialLinks>
          </FooterColumn>

          {/* Quick Links */}
          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink href="/">Home</FooterLink>
            <FooterLink href="/post">Create Image</FooterLink>
            <FooterLink href="">Gallery</FooterLink>
            <FooterLink href="/">About Us</FooterLink>
            <FooterLink href="/">Blog</FooterLink>
          </FooterColumn>

         

          {/* Contact Info */}
          <FooterColumn>
            <FooterTitle>Contact Us</FooterTitle>
            <ContactInfo>
              <Email />
              <div>
                <div>mohammadovescontact@gmail.com</div>
                <div>mohammadoves978@gmail.com</div>
              </div>
            </ContactInfo>
            <ContactInfo>
              <Phone />
              <div>
                <div>+91 9983449941</div>
               
              </div>
            </ContactInfo>
            
          </FooterColumn>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} <span>GemAI</span>. All rights reserved.
          </Copyright>
          <Copyright>
            Developed By <span>Mohammad Oves</span>
          </Copyright>
          <FooterLinks>
            <FooterBottomLink href="/">Privacy Policy</FooterBottomLink>
            <FooterBottomLink href="/">Terms of Service</FooterBottomLink>
            <FooterBottomLink href="/">Cookie Policy</FooterBottomLink>
          </FooterLinks>
        </FooterBottom>
      </FooterSection>
    </Container>
  );
};

export default Home;
